import React from 'react';

const Button = React.forwardRef(
  (
    {
      children,
      onClick,
      type = 'button',
      variant = 'solid',
      color = 'primary',
      size = 'md',
      disabled = false,
      loading = false,
      fullWidth = false,
      icon: Icon,
      iconPosition = 'left',
      isIconOnly = false,
      className = '',
      as: Component = 'button',
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center font-medium rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-all duration-200';

    const sizeStyles = {
      xs: 'px-2.5 py-1.5 text-xs gap-1.5',
      sm: 'px-3 py-2 text-sm gap-1.5',
      md: 'px-4 py-2.5 text-sm gap-2',
      lg: 'px-5 py-3 text-base gap-2',
      xl: 'px-6 py-3.5 text-base gap-2.5',
    };

    const iconOnlySizeStyles = {
      xs: 'p-1.5', sm: 'p-2', md: 'p-2.5', lg: 'p-3', xl: 'p-3.5',
    };

    const variantStyles = {
      solid: {
        primary: 'bg-brand-500 text-white hover:bg-brand-400 focus-visible:ring-brand-500 shadow-glow-sm hover:shadow-glow',
        secondary: 'bg-gray-600 text-white hover:bg-gray-500 focus-visible:ring-gray-500 dark:bg-gray-700 dark:hover:bg-gray-600',
        danger: 'bg-red-500 text-white hover:bg-red-400 focus-visible:ring-red-500',
        success: 'bg-accent-500 text-white hover:bg-accent-400 focus-visible:ring-accent-500 shadow-glow-accent',
        warning: 'bg-amber-500 text-white hover:bg-amber-400 focus-visible:ring-amber-500',
        brand: 'bg-gradient-to-r from-brand-500 to-accent-500 text-white hover:from-brand-400 hover:to-accent-400 focus-visible:ring-brand-500 shadow-glow-sm hover:shadow-glow',
      },
      outline: {
        primary: 'border border-brand-500/30 text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 focus-visible:ring-brand-500',
        secondary: 'border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 focus-visible:ring-gray-500',
        danger: 'border border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 focus-visible:ring-red-500',
        success: 'border border-accent-500/30 text-accent-600 dark:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20 focus-visible:ring-accent-500',
        warning: 'border border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 focus-visible:ring-amber-500',
        brand: 'border border-brand-500/30 text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 focus-visible:ring-brand-500',
      },
      ghost: {
        primary: 'text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 focus-visible:ring-brand-500',
        secondary: 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 focus-visible:ring-gray-500',
        danger: 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 focus-visible:ring-red-500',
        success: 'text-accent-600 dark:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20 focus-visible:ring-accent-500',
        warning: 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 focus-visible:ring-amber-500',
        brand: 'text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 focus-visible:ring-brand-500',
      },
      link: {
        primary: 'text-brand-600 dark:text-brand-400 hover:underline focus-visible:ring-brand-500',
        secondary: 'text-gray-600 dark:text-gray-300 hover:underline focus-visible:ring-gray-500',
        danger: 'text-red-600 dark:text-red-400 hover:underline focus-visible:ring-red-500',
        success: 'text-accent-600 dark:text-accent-400 hover:underline focus-visible:ring-accent-500',
        warning: 'text-amber-500 hover:underline focus-visible:ring-amber-500',
        brand: 'text-brand-600 dark:text-brand-400 hover:underline focus-visible:ring-brand-500',
      },
    };

    const disabledStyles = 'opacity-50 cursor-not-allowed pointer-events-none';
    const fullWidthStyles = fullWidth ? 'w-full' : '';
    const currentSizeStyles = isIconOnly ? iconOnlySizeStyles[size] : sizeStyles[size];

    const iconSize = { xs: 'h-3.5 w-3.5', sm: 'h-4 w-4', md: 'h-4 w-4', lg: 'h-5 w-5', xl: 'h-5 w-5' };

    const finalClassName = [
      baseStyles,
      currentSizeStyles,
      variantStyles[variant]?.[color] || variantStyles.solid.primary,
      (disabled || loading) && disabledStyles,
      fullWidthStyles,
      className,
    ].filter(Boolean).join(' ');

    return (
      <Component
        ref={ref}
        type={Component === 'button' ? type : undefined}
        onClick={onClick}
        disabled={disabled || loading}
        className={finalClassName}
        {...props}
      >
        {loading && (
          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
        {!loading && Icon && iconPosition === 'left' && <Icon className={iconSize[size]} />}
        {children}
        {!loading && Icon && iconPosition === 'right' && <Icon className={iconSize[size]} />}
      </Component>
    );
  }
);

Button.displayName = 'Button';
export { Button };
